import storefrontMetadata from './storefrontMetadata.json';
/**
 * HOW TO ADD A NEW DEALS STOREFRONT:
 * 1. Backend:
 *    a. Add a 'strategy=[new_strategy]' folder to S3 and add category CSV files inside it
 *    b. Update the storefrontId enum in the OpenAPI spec:
 *       HandshakePainterModel/openapi/v1/paths/storefront.yaml
 *    c. Add the strategy in:
 *       HandshakePainterService/.../data/AsinOverrideStrategy.java
 * 2. Frontend (update these three files):
 *    a. utils.ts — Add storefront key → UUID mapping to 'STOREFRONTS'
 *    b. storefrontMetadata.json — Add UUID entry with type: 'DEALS', heroImageUrl, fallbackUrl, strategy and valid categories
 *    c. storefrontPage.puff.json — Add category tab label translations. Key must be
 *       '{categoryName}Category' (the 'Category' suffix is required, e.g., 'electronicsCategory')
 *
 * For REGULAR storefronts (no backend changes needed):
 *    a. utils.ts — Add storefront key → UUID mapping to STOREFRONTS
 *    b. storefrontMetadata.json — Add UUID entry with type: 'REGULAR', products (ASIN's array),
 *       heroImageUrl, and fallbackUrl
 *    c. storefrontPage.puff.json — Add storefront name, caption, and description translations
 *
 * See README.md "Adding a new storefront" section for detailed backend changes (S3, OpenAPI, Service).
 */
export const STOREFRONTS = {
    defaultStorefront: '616d5a4d-5940-44e7-904a-f5d82d4ec8d5',
    jorpanda: '14c557ec-6ec1-43ae-b9de-f1173cb9d59b',
    kimxbeats: '99959a4b-4689-4865-8344-e3f19d6d31d2',
    denonxkaty: '707c8855-5c4b-455c-a224-7273b01608de',
    shopmodernmoments: '6c20bba0-002d-49cf-a4bb-0098067ab407',
    shopvsfs: '15303bc1-717e-4435-95a4-a78eadd2336c',
    beatsxkimpill: '3837f471-f1b3-4d18-b46a-2481eb4ab819',
    shopoura: '60c4f668-4124-43e3-aa5b-8390651261cd',
    smegshop: '802c276b-7ce0-4f41-ab21-5fd2381d02ff',
    redonebylg: 'c2f6016f-c742-4128-a077-468632c155bd',
    mariahcareyxmas: 'ba524349-21a5-4294-a6db-494171bb0fc5',
    shopparishilton: '78bb5baa-a455-4fe7-88b7-5b75f12401a3',
    ollywellness: 'd2791429-67cb-4141-91d4-7f638768fee6',
    dove: '9d198e9b-ad4f-4834-806b-5005b29806d1',
    halloween: '2e6b4d41-5739-4203-82cf-21f5e64a0f79',
    topfinds: '69d54adc-15ba-45b5-ba9b-01f5a63818ca',
    peakdeals: 'e395ce21-d56b-4a8c-bf64-7b9622f36e2b',
};
// Helper Functions
/** Sorts products so buyable items appear first. */
export const sortProducts = (products) => [...products].sort((a, b) => {
    if (a.buyable && !b.buyable)
        return -1;
    if (!a.buyable && b.buyable)
        return 1;
    return 0;
});
/**
 * Resolves a URL Id to a validated storefrontId and its typed metadata.
 * Falls back to 'defaultStorefront' for unknown or missing ids.
 */
export const resolveStorefront = (urlId) => {
    const isValid = !!urlId && urlId in STOREFRONTS;
    const id = isValid ? urlId : 'defaultStorefront';
    const meta = storefrontMetadata[STOREFRONTS[id]];
    return { id, isValid, meta };
};
/**
 * Validates storefrontCategories from URL search params against the metadata.
 * Returns empty arrays for REGULAR storefronts (they don't have strategy or categories).
 */
export const parseCategories = (meta, searchParams) => {
    var _a, _b;
    if (meta.type !== 'DEALS') {
        return { storefrontCategories: [], invalidCategories: [] };
    }
    const raw = (_b = (_a = searchParams.get('storefrontCategories')) === null || _a === void 0 ? void 0 : _a.split(',').filter(Boolean)) !== null && _b !== void 0 ? _b : [];
    return {
        storefrontCategories: raw.filter((cat) => meta.validCategories.includes(cat)),
        invalidCategories: raw.filter((cat) => !meta.validCategories.includes(cat)),
    };
};
